import { Grid, Typography } from "@material-ui/core";
import React from "react";
import moment from "moment";
import {
    buildingType,
    mitigationSystem,
    reportResultColor,
    structureType,
    testPurpose,
} from "../../../constants/values";
import { convertToPicocuries } from "../../../utils/convertToPicocuries";
import {
    calculateEpaAverage,
    reformatRadonAvgData,
} from "../../../utils/calculateEpaAverage";
import { getCompanyInfo } from "../../../models/services/authService";
import ReportPageHeader from "./ReportPageHeader";
import {
    BUILDING_TYPE_KEY,
    MEASUREMENT_UNIT,
    MITIGATION_SYSTEM_KEY,
    REPORT_RESULT_COLOR,
    REPORT_RESULT_SU,
    ROOM_TYPE_KEY,
    STRUCTURE_TYPE_KEY,
    TEST_FLOOR_KEY,
    UNIT_CONVERSION_SCALE,
} from "../../../constants/values";
import { isCanadaReport } from "../../../utils/reportCountry";
import ReportPageFooter from "./ReportPageFooter";

const styles = {
    sectionTitle: {
        color: "black",
        fontSize: "11px",
        fontWeight: 700,
        fontStyle: "italic",
        textAlign: "left",
        marginBottom: 4,
    },
    sectionDivider: {
        height: "4px",
        background: "#575195",
        marginBottom: 5,
        marginTop: 5,
    },
    labelCol: {
        color: "#333",
        fontSize: "10px",
        fontWeight: 400,
        textAlign: "left",
        lineHeight: "1.7",
    },
    valueCol: {
        color: "#000",
        fontSize: "10px",
        fontWeight: 500,
        textAlign: "left",
        lineHeight: "1.7",
    },
};

/** Two-column row: label on left, value on right */
const InfoRow = ({ label, value }) => {
    // Prevent React crash from rendering empty objects {} that APIs sometimes return
    const isPlainObject = typeof value === "object" && value !== null && !React.isValidElement(value) && !Array.isArray(value);
    const safeValue = isPlainObject ? (Object.keys(value).length === 0 ? "" : JSON.stringify(value)) : value;

    return (
        <Grid container style={{ paddingLeft: 35, paddingRight: 20 }}>
            <Grid item xs={6}>
                <Typography style={styles.labelCol}>{label}</Typography>
            </Grid>
            <Grid item xs={6}>
                <Typography style={styles.valueCol}>{safeValue ?? ""}</Typography>
            </Grid>
        </Grid>
    );
};

const SectionHeader = ({ title }) => (
    <div style={{ paddingLeft: 20, paddingRight: 20, marginTop: 5 }}>
        <div style={styles.sectionDivider} />
        <Typography style={styles.sectionTitle}>{title}</Typography>
    </div>
);

/**
 * PageMain for 48h report.
 * Supports both MU-web field names (camelCase) and EQWebDashboard field names (snake_case).
 */
const PageMain = ({ theme, reportDetails, reportChartRadonValue }) => {
    const [companyInfo, setCompanyInfo] = React.useState(undefined);
    const isCanada = isCanadaReport(reportDetails);

    const renderUnitText = (text) => {
        if (typeof text !== "string") return text;
        const target = "Bq/m3";
        const index = text.indexOf(target);
        if (index === -1) return text;

        const prefix = text.slice(0, index);
        const suffix = text.slice(index + target.length);
        return (
            <>
                {prefix}
                Bq/m<sup>3</sup>
                {suffix}
            </>
        );
    };

    React.useEffect(() => {
        if (!reportDetails) return;
        const fetchCompanyInfo = async () => {
            try {
                const targetEmail = reportDetails.email ?? reportDetails.test_by?.ownerDisplay ?? "";
                if (!targetEmail) return;

                const trimmedEmail = targetEmail.trim().toLowerCase();
                const res = await getCompanyInfo(trimmedEmail);
                if (res && res.data) {
                    setCompanyInfo(res.data);
                } else {
                    setCompanyInfo(null);
                }
            } catch (err) {
                console.error("Failed to load company info", err);
                setCompanyInfo(null);
            }
        };
        fetchCompanyInfo();
    }, [reportDetails]);

    if (!reportDetails) return null;

    /* ---------- helper fns ---------- */
    const reformatRadonData = (data, unit, point) => {
        if (data === "" || data === undefined || data === null || data === -1)
            return "";
        if (unit === 0) return (data / 37).toFixed(point) + " pCi/L";
        return data.toFixed(0) + " Bq/m3";
    };

    // Support both camelCase (MU-web API) and snake_case (EQWebDashboard API)
    const measurementUnitVal =
        reportDetails.measurementUnit ?? reportDetails.measurement_unit ?? 0;
    const radonAvg =
        reportDetails.radonAvg ?? reportDetails.avg_radon;
    const serialNumber =
        reportDetails.serialNumber ?? reportDetails.serial_number;
    let calibrationExpired =
        reportDetails.calibrationExpired ?? reportDetails.calibration_date;
    if (calibrationExpired) {
        const m = moment(calibrationExpired);
        if (m.isValid()) {
            calibrationExpired = m.add(1, "years").format("MM/DD/YYYY");
        }
    }

    const propertyAddress = () => {
        const a = reportDetails.device_location;
        if (!a) return "";
        return [a.address1 ?? a.street, a.address2, a.city, a.state, a.zipcode]
            .filter(Boolean)
            .join(" ");
    };

    const stateCode = String(reportDetails?.device_location?.state || "").trim().toUpperCase();
    const isIllinois = stateCode === "IL" || stateCode === "ILLINOIS";
    const dataPointThreshold = isIllinois ? 44 : 46;
    const dataPointCount = Array.isArray(reportDetails?.radondata) ? reportDetails.radondata.length : 0;
    const hasEnoughDataPoints = dataPointCount > dataPointThreshold;

    const epaAverageRaw = calculateEpaAverage(reportChartRadonValue);
    const epaAverageNumeric = Number(epaAverageRaw);
    const hasEpaAverage = Number.isFinite(epaAverageNumeric);

    let epaAveragePci = null;
    if (hasEpaAverage) {
        epaAveragePci =
            measurementUnitVal === 1
                ? Number(convertToPicocuries(epaAverageNumeric))
                : epaAverageNumeric;
    }

    const epaAverageRoundedPci =
        epaAveragePci !== null && Number.isFinite(epaAveragePci)
            ? Number(epaAveragePci.toFixed(1))
            : null;

    const hasActionLevelResult =
        hasEnoughDataPoints &&
        epaAverageRoundedPci !== null &&
        Number.isFinite(epaAverageRoundedPci);

    let testResultLabel = "";
    let testResultColor = "black";
    if (hasActionLevelResult) {
        if (epaAverageRoundedPci >= 4.0) {
            testResultLabel = "≥ EPA Action Level";
            testResultColor = reportResultColor[0] ?? "black";
        } else {
            testResultLabel = "< EPA Action Level";
            testResultColor = reportResultColor[1] ?? "black";
        }
    }

    const overallAvg = reformatRadonData(radonAvg, measurementUnitVal, 1);
    const epaAvg = hasEnoughDataPoints
        ? reformatRadonAvgData(epaAverageRaw, measurementUnitVal, 1)
        : "";

    // Client info (support both structures)
    const clientName = reportDetails.client ? reportDetails.client.client_name : reportDetails.client_name;
    const clientEmail = reportDetails.client ? reportDetails.client.client_display : reportDetails.client_display ? reportDetails.client_display : reportDetails.client_email;
    const clientPhone = reportDetails.client ? reportDetails.client.client_phone : reportDetails.client_phone;
    const locationId = reportDetails.device_name;

    // Placement info (EQWebDashboard uses device_placement)
    const bldType =
        reportDetails.buildingType ?? reportDetails.device_placement?.bld_type;
    const strType =
        reportDetails.structureType ?? reportDetails.device_placement?.str_type;
    const mitigation =
        reportDetails.mitigationSystem ??
        reportDetails.device_placement?.mitigation_installed;
    const yearBuilt =
        reportDetails.yearBuilt ?? reportDetails.device_placement?.year_built;

    return (
        <div style={{ height: "750px", overflow: "hidden", position: "relative" }}>
            {/* ===== Shared Header ===== */}
            <ReportPageHeader reportDetails={reportDetails} />

            {/* ===== Title ===== */}
            <Typography
                style={{
                    textAlign: "center",
                    fontWeight: 900,
                    color: "black",
                    fontSize: "24px",
                    marginTop: 5,
                    marginBottom: 0,
                }}
            >
                {isCanada ? "Radon Report / Rapport sur le radon" : "Radon Report"}
            </Typography>

            <SectionHeader title={isCanada ? "Tested By/ Teste par" : "Tested By"} />
            <InfoRow
                label={isCanada ? "Company Name/Nom de l'entreprise" : "Company Name"}
                value={companyInfo === undefined ? "" : (companyInfo?.companyName || "")}
            />
            <InfoRow
                label={isCanada ? "Address/Adresse" : "Address"}
                value={companyInfo === undefined ? "" : (
                    <>
                        {companyInfo?.address?.street && (
                            <>
                                {companyInfo.address.street}
                                {companyInfo.address.detail ? " " + companyInfo.address.detail : ""}
                                {" "}
                            </>
                        )}
                        {companyInfo?.address && [companyInfo.address.city, companyInfo.address.state, companyInfo.address.zipcode].filter(Boolean).join(" ")}
                    </>
                )}
            />

            {/* ===== PROPERTY INFORMATION ===== */}
            <SectionHeader title={isCanada ? "Property Information/Renseignements sur la propriété" : "Property Information"} />
            <InfoRow label={isCanada ? "Property Name/Nom de la propriété" : "Property Name"} value={locationId} />
            <InfoRow label={isCanada ? "Contact Person/Personne-ressource" : "Contact Person"} value={clientName} />
            <InfoRow label={isCanada ? "Address/Adresse" : "Address"} value={propertyAddress()} />
            <InfoRow label={isCanada ? "Phone/Telephone" : "Phone"} value={clientPhone} />
            <InfoRow label={isCanada ? "Email/Courriel" : "Email"} value={clientEmail} />
            <div style={{ height: 4 }} />
            <InfoRow label={isCanada ? "Building Type/Type de batiment" : "Building Type"} value={reportDetails.device_placement ? BUILDING_TYPE_KEY[reportDetails.device_placement.bld_type]
                ? BUILDING_TYPE_KEY[reportDetails.device_placement.bld_type] : "N/A"
                : "N/A"} />
            <InfoRow label={isCanada ? "Structure Type/Type de structure" : "Structure Type"} value={reportDetails.device_placement ? STRUCTURE_TYPE_KEY[reportDetails.device_placement.str_type]
                ? STRUCTURE_TYPE_KEY[reportDetails.device_placement.str_type] : "N/A"
                : "N/A"} />
            <InfoRow label={isCanada ? "Mitigation System/Systeme d'attenuation" : "Mitigation System"} value={reportDetails.device_placement ? MITIGATION_SYSTEM_KEY[
                reportDetails.device_placement.mitigation_installed
            ]
                ? MITIGATION_SYSTEM_KEY[
                reportDetails.device_placement.mitigation_installed
                ] : "N/A"
                : "N/A"} />
            <InfoRow label={isCanada ? "Year Built/Annee de construction" : "Year Built"} value={reportDetails.device_placement ? reportDetails.device_placement.year_built
                ? reportDetails.device_placement.year_built : "N/A"
                : "N/A"} />

            {/* ===== TEST RESULT ===== */}
            {isCanada ? (
                <div style={{ paddingLeft: 20, paddingRight: 20, marginTop: 5 }}>
                    <div style={styles.sectionDivider} />
                    <Grid
                        container
                        alignItems="center"
                        style={{ minHeight: "68px", paddingLeft: 15, paddingRight: 0, paddingTop: 2, paddingBottom: 2 }}
                    >
                        <Grid item xs={6}>
                            <Typography style={styles.sectionTitle}>
                                Test Result/Resultat du test:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Grid container alignItems="center" style={{ marginBottom: 0 }}>
                                <Grid item xs={4}>
                                    <Typography
                                        style={{
                                            color: "#000",
                                            fontWeight: 400,
                                            fontSize: "11px",
                                            textAlign: "left",
                                        }}
                                    >
                                        Overall Average/ Moyenne globale
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography
                                        style={{ color: "black", fontSize: "18px", fontWeight: 700, marginLeft: 10 }}
                                    >
                                        {renderUnitText(overallAvg)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            ) : (
                <>
                    <SectionHeader title="Test Result:" />
                    <Grid container style={{ paddingLeft: 35, paddingRight: 20, marginTop: 4 }}>
                        {/* Left: big result text */}
                        <Grid item xs={6}>
                            <Typography
                                style={{
                                    color: testResultColor,
                                    fontSize: "18px",
                                    fontWeight: 1000,
                                    textAlign: "left",
                                }}
                            >
                                {testResultLabel}
                            </Typography>
                            <Typography
                                style={{
                                    color: "#696F8C",
                                    fontSize: "8px",
                                    fontWeight: 400,
                                    marginTop: 6,
                                    textAlign: "left",
                                    whiteSpace: "pre-line",
                                }}
                            >
                                EPA recommends mitigation if radon is at or above the EPA Action Level (4.0 pCi/L).
                            </Typography>
                        </Grid>
                        {/* Right: averages */}
                        <Grid item xs={6}>
                            <Grid container alignItems="center" style={{ marginBottom: 0 }}>
                                <Grid item xs={4}>
                                    <Typography style={{ color: "#000", fontWeight: 400, fontSize: "11px", textAlign: "left" }}>
                                        Overall Average
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography style={{ color: "black", fontSize: "18px", fontWeight: 700 }}>
                                        {renderUnitText(overallAvg)}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container alignItems="center">
                                <Grid item xs={4}>
                                    <Typography style={{ color: "#000", fontWeight: 400, fontSize: "11px", textAlign: "left" }}>
                                        EPA Average*
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography style={{ color: "black", fontSize: "18px", fontWeight: 700 }}>
                                        {renderUnitText(epaAvg)}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Typography style={{ color: "#696F8C", fontWeight: 400, fontSize: "8px", marginTop: 4, textAlign: "left" }}>
                                *The EPA protocol average discards the first four hours of the
                                deployment period. The official number to go with is the EPA
                                protocol average.
                            </Typography>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* ===== DEVICE INFORMATION ===== */}
            <SectionHeader title={isCanada ? "Device Information/Renseignments sur l'appareil" : "Device Information"} />
            <InfoRow label={isCanada ? "Device Name/Nom de l'appareil" : "Device Name"} value="EcoGuard" />
            <InfoRow label={isCanada ? "Model Number/Numero de modele" : "Model Number"} value="EQ200EG" />
            <InfoRow label={isCanada ? "Serial Number/Numero de serie" : "Serial Number"} value={serialNumber} />
            <InfoRow label={isCanada ? "Manufacturer/Fabricant" : "Manufacturer"} value="FTLab" />
            <InfoRow label={isCanada ? "Calibration Exp. Date/Date d'expiration de l'etalonnage" : "Calibration Exp. Date"} value={calibrationExpired ?? ""} />
            <InfoRow
                label={isCanada ? "Device License/Licence par appareil" : "Device License"}
                value={isCanada ? "C-NRPP12456" : "NRPP 6346"}
            />
            {!isCanada && (
                <>
                    <InfoRow label="MDC*" value="0.2 pCi/L" />
                    <Typography
                        style={{
                            color: "#696F8C",
                            fontSize: "8px",
                            paddingLeft: 35,
                            marginTop: 2,
                            textAlign: "left",
                        }}
                    >
                        *Minimum Detectable Concentration
                    </Typography>
                </>
            )}
            <ReportPageFooter reportDetails={reportDetails} />
        </div>
    );
};

export default PageMain;
